import clsx from 'clsx';
import cloneDeep from 'lodash/cloneDeep';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChangeProfileType,
  Dialog,
  DropDown,
  EditableField,
  EmailProfiles,
  GroupCard,
  GroupDetailsView,
  GroupTeamMembers,
  Header,
  NewGroup,
  NewGroupColumn,
  Popover
} from '../components';
import Button from '../components/common/button';
import {
  ArchiveIcon,
  CancelIcon,
  EditIcon,
  ExportIcon,
  PdfIcon,
  PlusButtonIcon,
  PlusIcon,
  SortIcon
} from '../components/icons';
import { ProfileContext } from '../contexts/profileContext';
import { permissions } from '../data/permissions';
import useOrganizationColors from '../hooks/useOrganizationColors';
import {
  deleteGroupColumn,
  disableGroupColumn,
  generateBundlePdf,
  getGroups,
  getProfileDataForExport,
  updateGroupColumn,
  updateColumnOrder
} from '../service';
import { getSortedColumns } from '../utils';
import { exportDataSingleSheet, hexToRgba } from '../utils/utils';
import { DotsThree } from '@phosphor-icons/react';

// Sortable Column Component
function SortableColumn({
  groupColumnItem,
  canManageGroups,
  groupColumnOptions,
  colors,
  sortOptions,
  handleAddNewGroupClick,
  sortGroups,
  handleGroupClick,
  exportingGroups,
  exportingPDFs,
  t,
  i18n,
  getAllGroups,
  setGroups,
  setExportingGroups,
  setExportingPDFs,
  handleGroupColumnNameUpdate
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: groupColumnItem._id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition || 'transform 200ms ease',
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    scale: isDragging ? 1.02 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={clsx(
        'p-3 w-[300px] flex-shrink-0 bg-white rounded h-full relative group',
        'transition-all duration-200 ease-in-out',
        isDragging && 'shadow-lg ring-2 ring-blue-200 bg-blue-50'
      )}>
      {/* Drag handle - positioned on top border, only visible on hover */}
      {canManageGroups && (
        <div
          {...listeners}
          className="absolute top-16 left-1/2 transform -translate-x-1/2 cursor-grab active:cursor-grabbing p-1.5 bg-textAndIcons rounded-full border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transition-all duration-150 z-10 opacity-0 group-hover:opacity-100"
          title="Drag to reorder">
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            className="text-white hover:text-gray-600">
            <path
              fill="currentColor"
              d="M3 7a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm5-6a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm5-6a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0 3a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
            />
          </svg>
        </div>
      )}

      {/* Rest of the column content */}
      <div>
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <EditableField
              isEditable={groupColumnItem._id !== 'not-bundled' && canManageGroups}
              label={t('groupColumn.columnNameField')}
              text={groupColumnItem.name}
              id={groupColumnItem._id}
              submitFunction={updateGroupColumn}
              onUpdate={handleGroupColumnNameUpdate}
              DisplayComponent="h3"
            />
          </div>
          {canManageGroups && groupColumnOptions?.length > 0 && (
            <Popover
              position="bottom right"
              size="sm"
              button={
                <DotsThree size={32} className="cursor-pointer rounded hover:bg-orange-100" />
              }>
              <div className="p-2">
                <ul className="text-tertiary space-y-2">
                  {groupColumnOptions.map((actionItem) => (
                    <li key={actionItem.id}>
                      {actionItem.popover ? (
                        <Popover
                          position="bottom right"
                          size="normal"
                          button={
                            <button
                              type="button"
                              className={clsx(
                                'flex items-center gap-2 rounded hover:bg-orange-100 px-1 cursor-pointer w-full text-left',
                                actionItem.className
                              )}>
                              <actionItem.icon className="w-4 h-4" />
                              <span>{actionItem.name}</span>
                            </button>
                          }>
                          {actionItem.popover}
                        </Popover>
                      ) : (
                        <button
                          type="button"
                          onClick={async () => {
                            if (actionItem.id === 'delete') {
                              try {
                                await deleteGroupColumn(groupColumnItem._id);
                                toast.success('Group column deleted successfully.');
                                getAllGroups();
                              } catch (error) {
                                if (error.response.data.includes('inactive organization')) {
                                  toast.error(error.response.data);
                                } else {
                                  toast.error('Failed to delete the group column.');
                                }
                              }
                            } else if (actionItem.id === 'disable') {
                              try {
                                await disableGroupColumn(groupColumnItem._id);
                                toast.success('Group column disabled successfully.');
                                getAllGroups();
                              } catch (error) {
                                console.error('Failed to disable the group column.');
                                toast.error('Failed to disable the group column:', error);
                              }
                            } else if (actionItem.id === 'export-data') {
                              try {
                                // Set loading state for this specific group
                                setExportingGroups(
                                  (prev) => new Set(prev.add(groupColumnItem._id))
                                );

                                const { data: exportList } = await getProfileDataForExport(
                                  'groupColumnId',
                                  groupColumnItem._id,
                                  i18n.language
                                );
                                exportDataSingleSheet(
                                  [{ key: 'email', name: 'Email' }, ...exportList.headers],
                                  exportList.data,
                                  groupColumnItem.name,
                                  t,
                                  i18n.language,
                                  groupColumnItem.name,
                                  'Group'
                                );
                              } catch (error) {
                                console.error('Failed to export data.');
                                toast.error('Failed to export data:', error);
                              } finally {
                                // Remove loading state for this specific group
                                setExportingGroups((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(groupColumnItem._id);
                                  return newSet;
                                });
                              }
                            } else if (actionItem.id === 'export-pdf') {
                              try {
                                // Set loading state for this specific group
                                setExportingPDFs((prev) => new Set(prev.add(groupColumnItem._id)));

                                await generateBundlePdf({
                                  columnId: groupColumnItem._id,
                                  languageId: i18n.language
                                });
                                toast.success('Bundle pdf request sent successfully.');
                              } catch (error) {
                                console.error('Failed to export PDF.');
                              } finally {
                                // Remove loading state for this specific group
                                setExportingPDFs((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(groupColumnItem._id);
                                  return newSet;
                                });
                              }
                            }
                          }}
                          className={clsx(
                            'flex items-center gap-2 rounded hover:bg-orange-100 px-1 cursor-pointer w-full text-left',
                            actionItem.className,
                            exportingGroups.has(groupColumnItem._id) &&
                              actionItem.id === 'export-data' &&
                              'opacity-50 cursor-not-allowed'
                          )}
                          disabled={
                            (exportingGroups.has(groupColumnItem._id) &&
                              actionItem.id === 'export-data') ||
                            (exportingPDFs.has(groupColumnItem._id) &&
                              actionItem.id === 'export-pdf')
                          }>
                          {exportingGroups.has(groupColumnItem._id) &&
                          actionItem.id === 'export-data' ? (
                            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                          ) : exportingPDFs.has(groupColumnItem._id) &&
                            actionItem.id === 'export-pdf' ? (
                            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <actionItem.icon className="w-4 h-4" />
                          )}
                          <span>{actionItem.name}</span>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Popover>
          )}
        </div>
        <span
          className={clsx(
            'text-xs block w-fit rounded-full px-1.5 py-0.5',
            groupColumnItem.groups?.length === 0 ? 'mt-0' : '-mt-2',
            groupColumnItem._id === 'not-bundled' ? 'invisible' : 'text-gray-500'
          )}
          style={{
            backgroundColor: hexToRgba(colors.secondaryVariant, colors.intensitySecondary)
          }}>
          {groupColumnItem.profileType || '\u00A0'}
        </span>
      </div>
      <div className={'mt-4 ring-1 ring-inset ring-black rounded-md p-4 h-[98%]'}>
        <div
          className={clsx('flex items-center ', {
            'justify-between': groupColumnItem._id !== 'not-bundled' && canManageGroups,
            'justify-end': groupColumnItem._id === 'not-bundled' || !canManageGroups
          })}>
          {groupColumnItem._id !== 'not-bundled' && canManageGroups && (
            <PlusButtonIcon
              onClick={() => handleAddNewGroupClick(groupColumnItem)}
              className="cursor-pointer"
            />
          )}
          <DropDown
            colors={colors}
            options={sortOptions}
            onItemSelect={(selectedOption) => sortGroups(groupColumnItem._id, selectedOption)}>
            {groupColumnItem.groups?.length > 0 && <SortIcon className="cursor-pointer" />}
          </DropDown>
        </div>
        <ul className="overflow-y-auto h-[98%]">
          {groupColumnItem.groups.map((groupItem) => (
            <GroupCard key={groupItem._id} groupItem={groupItem} onClick={handleGroupClick} />
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { groupId } = useParams();
  const [profile] = useContext(ProfileContext);
  const colors = useOrganizationColors();

  const profilePermissionIds = profile.permissions?.map((permission) => permission._id);

  const canManageGroups =
    profile.role === 'systemAdmin' ||
    (profile.role === 'systemSubAdmin' &&
      profilePermissionIds.includes(permissions.CREATE_ORGANIZATION));

  const canManageProfiles =
    profile.role === 'systemAdmin' ||
    (profile.role === 'systemSubAdmin' &&
      profilePermissionIds
        .map((permission) => permission)
        .includes(permissions.CREATE_ORGANIZATION)) ||
    profile.role === 'organizationAdmin' ||
    (profile.role === 'organizationSubAdmin' &&
      profilePermissionIds.includes(permissions.MANAGE_ORGANIZATION_ADMINS));

  // console.log('permissions.MANAGE_ORGANIZATION_ADMINS', permissions.MANAGE_ORGANIZATION_ADMINS);
  // console.log('profilePermissionIds', profilePermissionIds);

  // console.log({ canManageProfiles });

  const [groups, setGroups] = useState([]);
  const [columnOrder, setColumnOrder] = useState(profile.columnOrder || []);
  const [showAddNewGroup, setShowAddNewGroup] = useState(false);
  const [showAddNewGroupColumn, setShowAddNewGroupColumn] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState({ isOpen: false });
  const [selectedGroupColumn, setSelectedGroupColumn] = useState(null);
  const [exportingGroups, setExportingGroups] = useState(new Set());
  const [exportingPDFs, setExportingPDFs] = useState(new Set());

  const [sortOptions, setSortOptions] = useState([
    {
      id: 'alphabetically',
      name: t('groupColumn.sort.alphabetically'),
      isSelected: true
    },
    {
      id: 'most-recent',
      name: t('groupColumn.sort.recent')
    },
    {
      id: 'oldest',
      name: t('groupColumn.sort.oldest')
    }
  ]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // Require 8px of movement before activating
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const getAllGroups = async () => {
    try {
      const { data: groupsList } = await getGroups();

      setGroups(groupsList);
    } catch (e) {
      toast.error('Failed to get groups.');
      console.error('error:', e);
    }
  };

  useEffect(() => {
    getAllGroups();
  }, [groupId]);

  // Sync columnOrder state with profile changes
  useEffect(() => {
    if (profile.columnOrder) {
      setColumnOrder(profile.columnOrder);
    }
  }, [profile.columnOrder]);

  useEffect(() => {
    if (groups?.length && groupId) {
      const searchedGroup = groups
        .flatMap((group) => group.groups)
        .find((subGroup) => subGroup._id === groupId);

      if (searchedGroup) {
        setSelectedGroup((prev) => ({
          ...searchedGroup,
          screen: prev?.screen || 'main',
          isOpen: true
        }));
      }
    }
  }, [groups, groupId]);

  const handleGroupClick = (group) => {
    const totalProfiles = group.notStarted + group.inProgress + group.validated;
    setSelectedGroup({ ...group, totalProfiles, screen: 'main', isOpen: true });
  };

  const handleGroupViewClose = () => {
    setSelectedGroup({ ...selectedGroup, isOpen: false });
  };

  const handleAddNewGroupClick = (groupColumn) => {
    setShowAddNewGroup(true);
    setSelectedGroupColumn(groupColumn);
  };

  const sortGroups = (groupColumnId, selectedOption) => {
    const updatedGroups = cloneDeep(groups);
    const groupObj = updatedGroups.find((group) => group._id === groupColumnId);

    if (!groupObj) {
      console.error('Group not found');
      return;
    }

    switch (selectedOption.id) {
      case 'alphabetically':
        groupObj.groups.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'most-recent':
        groupObj.groups.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        groupObj.groups.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      default:
        console.error('Invalid sort option');
    }
    setGroups(updatedGroups);

    const updatedSortOptions = cloneDeep(sortOptions);
    updatedSortOptions.forEach((sortOption) => (sortOption.isSelected = false));
    updatedSortOptions.find((sortOption) => sortOption.id === selectedOption.id).isSelected = true;

    setSortOptions(updatedSortOptions);
  };

  const handleGroupColumnNameUpdate = ({ id, fieldText }) => {
    setGroups((groups) =>
      groups.map((groupColumnItem) =>
        groupColumnItem._id === id ? { ...groupColumnItem, name: fieldText } : groupColumnItem
      )
    );
  };

  const handleGroupNameUpdate = ({ id, fieldText }) => {
    setSelectedGroup({ ...selectedGroup, name: fieldText });

    setGroups((groups) =>
      groups.map((groupColumnItem) => ({
        ...groupColumnItem,
        groups: groupColumnItem.groups.map((group) =>
          group._id === id ? { ...group, name: fieldText } : group
        )
      }))
    );
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = groups.findIndex((item) => item._id === active.id);
      const newIndex = groups.findIndex((item) => item._id === over.id);

      const newGroups = arrayMove(groups, oldIndex, newIndex);
      const newColumnOrder = newGroups.map((group) => group._id);

      // Update both groups and columnOrder immediately for instant UI feedback
      setGroups(newGroups);
      setColumnOrder(newColumnOrder);

      // Save the new order to the database
      try {
        await updateColumnOrder(newColumnOrder);
      } catch (error) {
        console.error('Failed to save column order:', error);
        toast.error('Failed to save column order.');
        // Revert both changes on error
        setGroups(groups);
        setColumnOrder(profile.columnOrder || []);
      }
    }
  };

  return (
    <>
      <div className="App">
        <div className={'h-screen flex'}>
          <div className={'flex-1 min-w-0 bg-white flex flex-col'}>
            <Header />

            <div className={'flex-1 h-[90vh] overflow-y-hidden'}>
              <main className={'px-4 py-12 inline-flex gap-4 h-full'}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={getSortedColumns(groups, columnOrder)?.map((item) => item._id) || []}
                    strategy={horizontalListSortingStrategy}>
                    {getSortedColumns(groups, columnOrder)?.map((groupColumnItem) => {
                      let groupColumnOptions = [];

                      if (canManageGroups && groupColumnItem.groups?.length === 0) {
                        groupColumnOptions.push({
                          id: 'delete',
                          name: 'Delete',
                          icon: CancelIcon
                        });
                      }

                      const hasGroups = groupColumnItem.groups?.length > 0;
                      if (hasGroups && canManageGroups) {
                        groupColumnOptions.unshift({
                          id: 'disable',
                          name: 'Disable & Store',
                          icon: ArchiveIcon
                        });
                        if (groupColumnItem._id !== 'not-bundled') {
                          groupColumnOptions.unshift({
                            id: 'export-data',
                            name: 'Export Data',
                            icon: ExportIcon
                          });
                        }
                        if (!hasGroups && canManageGroups) {
                          groupColumnOptions.push({
                            id: 'delete',
                            name: 'Delete',
                            icon: CancelIcon
                          });
                        }

                        if (canManageGroups && groupColumnItem._id !== 'not-bundled') {
                          // export pdf
                          groupColumnOptions.unshift({
                            id: 'export-pdf',
                            name: 'Export PDF',
                            icon: PdfIcon
                          });

                          groupColumnOptions.unshift({
                            id: 'change-profile-type',
                            name: 'Change Profile Type',
                            icon: EditIcon,
                            popover: (
                              <ChangeProfileType
                                groupColumn={groupColumnItem}
                                onSubmit={async (column, profileType) => {
                                  try {
                                    await updateGroupColumn(column, {
                                      type: 'profile-type',
                                      profileType: profileType._id
                                    });
                                    toast.success('Group column profile type saved successfully.');
                                    getAllGroups();
                                    // update the group column item with the new profile type
                                    setGroups((prev) =>
                                      prev.map((group) =>
                                        group._id === column
                                          ? { ...group, profileType: profileType.label }
                                          : group
                                      )
                                    );
                                  } catch (error) {
                                    console.error('Failed to update group column:', error);
                                    toast.error('Failed to update group column.');
                                  }
                                }}
                              />
                            )
                          });
                        }
                      }
                      return (
                        <SortableColumn
                          key={groupColumnItem._id}
                          groupColumnItem={groupColumnItem}
                          canManageGroups={canManageGroups}
                          groupColumnOptions={groupColumnOptions}
                          colors={colors}
                          sortOptions={sortOptions}
                          handleAddNewGroupClick={handleAddNewGroupClick}
                          sortGroups={sortGroups}
                          handleGroupClick={handleGroupClick}
                          exportingGroups={exportingGroups}
                          exportingPDFs={exportingPDFs}
                          t={t}
                          i18n={i18n}
                          getAllGroups={getAllGroups}
                          setGroups={setGroups}
                          setExportingGroups={setExportingGroups}
                          setExportingPDFs={setExportingPDFs}
                          handleGroupColumnNameUpdate={handleGroupColumnNameUpdate}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
                {canManageGroups && (
                  <Button.ButtonPlatform
                    type="button"
                    className="mt-16"
                    onClick={() => setShowAddNewGroupColumn(true)}>
                    <PlusIcon className="mb-1" /> {t('groupColumn.addNewButton')}
                  </Button.ButtonPlatform>
                )}
              </main>
            </div>
          </div>
        </div>
      </div>
      {/* ADD NEW GROUP */}
      <NewGroup
        groupColumn={selectedGroupColumn}
        showAddNewGroup={showAddNewGroup}
        setShowAddNewGroup={setShowAddNewGroup}
        onUpdate={() => getAllGroups()}
      />
      {/* ADD NEW GROUP COLUMN */}
      <NewGroupColumn
        showAddNewGroupColumn={showAddNewGroupColumn}
        setShowAddNewGroupColumn={setShowAddNewGroupColumn}
        onUpdate={() => getAllGroups()}
      />
      {/* SHOW GROUP DETAILS */}
      <Dialog
        size="3xl"
        show={selectedGroup?.isOpen}
        header={selectedGroup?.screen !== 'main' ? selectedGroup?.name : null}
        onBackButtonClick={
          selectedGroup?.screen === 'main'
            ? null
            : () => setSelectedGroup({ ...selectedGroup, screen: 'main' })
        }
        onClose={handleGroupViewClose}>
        {selectedGroup?.screen === 'main' && (
          <GroupDetailsView
            group={selectedGroup}
            canManageGroups={canManageGroups}
            canManageProfiles={canManageProfiles}
            onUpdate={(updates) => {
              setSelectedGroup((prev) => ({ ...prev, ...updates }));
              const updateKeys = Object.keys(updates || {});
              const isScreenOnlyUpdate = updateKeys.length === 1 && updateKeys[0] === 'screen';
              if (!isScreenOnlyUpdate) {
                getAllGroups();
              }
            }}
            onNameUpdate={handleGroupNameUpdate}
          />
        )}
        {selectedGroup?.screen === 'email-profiles' && (
          <EmailProfiles groupId={selectedGroup?._id} />
        )}
        {selectedGroup?.screen === 'team-members' && (
          <GroupTeamMembers groupId={selectedGroup?._id} />
        )}
      </Dialog>
    </>
  );
}
